import { AdjustmentsHorizontal } from "flowbite-react-icons/solid";
import { useEffect, useRef, useState } from "react";
import DevTiltifyImpersonate from "@/components/shared/dev-tiltify-impersonate";
import useTiltifyConfig from "@/hooks/useTiltifyConfig";
import useTwitchConfig from "@/hooks/useTwitchConfig";

export default function DevModal() {
  const { config: tiltifyConfig, setFromEvent: setTiltifyFromEvent } = useTiltifyConfig();
  const { config: twitchConfig, setFromEvent: setTwitchFromEvent } = useTwitchConfig();
  const [error, setError] = useState<string | null>(null);
  const [configText, setConfigText] = useState("");
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const splitHost = window.location.host.split(".");
  const modalRef = useRef<HTMLDialogElement | null>(null);
  let environment: string = "prod";

  if (splitHost.length > 1) {
    const hostEnvironment = splitHost[1];

    switch (hostEnvironment) {
      case "dev":
      case "qa":
        environment = hostEnvironment;
        break;
      case "experience":
        environment = "prod";
    }
  } else {
    environment = import.meta.env.VITE_APP_ENV ?? "prod";
  }

  useEffect(() => {
    const handleModalClose = () => {
      document.body.classList.remove("show-modal");
    };

    const modal = modalRef.current;

    if (modal) {
      modal.addEventListener("close", handleModalClose);
    }
    return () => {
      if (modal) {
        modal.removeEventListener("close", handleModalClose);
      }
    };
  }, []);

  function setModalRef(div: HTMLDialogElement | null) {
    modalRef.current = div;
  }

  function showModal() {
    if (modalRef.current) {
      document.body.classList.add("show-modal");
      modalRef.current.showModal();
    }
  }

  function closeModal() {
    modalRef.current?.close();
  }

  function resetSettings(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    const emptyConfig: Record<string, unknown> = {
      ...tiltifyConfig,
      ...twitchConfig,
    };

    Object.keys(emptyConfig).forEach((key) => {
      if (key === "tiltifyCampaignGoal") {
        emptyConfig[key] = "0";
      } else {
        emptyConfig[key] = "";
      }
    });

    const emptyConfigString = JSON.stringify(emptyConfig, null, 2);
    setConfigText(emptyConfigString);
    parseSetConfig(emptyConfigString);
  }

  function applyChanges(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    parseSetConfig(configText);
  }

  function parseSetConfig(configString: string) {
    setError(null);
    try {
      const newConfig = JSON.parse(configString);

      Object.keys(newConfig).forEach((key) => {
        if (key.includes("tiltify")) {
          setTiltifyFromEvent({
            target: { name: key, value: newConfig[key] },
          });
        } else if (key.includes("twitch")) {
          setTwitchFromEvent({
            target: { name: key, value: newConfig[key] },
          });
        }
      });
    } catch (e) {
      const err = e as Error;
      setError(err.message ?? "Invalid JSON");
    }
  }

  const textareaValue = JSON.stringify(
    {
      ...tiltifyConfig,
      ...twitchConfig,
    },
    null,
    2,
  );

  useEffect(() => {
    setConfigText(textareaValue);
  }, [textareaValue]);

  if (environment !== "dev") {
    return null;
  }

  return (
    <div className="absolute bottom-3 right-3 z-50 pt-5 pl-5">
      <button
        type="button"
        className="btn btn-circle btn-secondary mr-4 shadow-xl"
        onClick={showModal}
      >
        <AdjustmentsHorizontal />
      </button>
      <dialog ref={setModalRef} id="devModal" className="modal">
        <div className="modal-box">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-bold">Developer Settings</h3>
            <button
              type="button"
              className="btn btn-sm btn-circle btn-ghost"
              aria-label="Close developer settings"
              onClick={closeModal}
            >
              ✕
            </button>
          </div>

          <DevTiltifyImpersonate />

          <div className="relative mt-1">
            <textarea
              ref={textAreaRef}
              spellCheck={false}
              className="textarea textarea-bordered text-sm w-full min-h-96 overflow-x-hidden overflow-y-auto resize-none"
              placeholder="{}"
              value={configText}
              onChange={(event) => setConfigText(event.target.value)}
            />

            <div className="min-h-10">
              <span className="text-xs text-error">{error ? error : ""}</span>
            </div>
          </div>
          <div className="modal-action">
            <form method="dialog" className="flex w-full">
              <button type="button" className="btn btn-sm btn-primary mr-1" onClick={resetSettings}>
                Reset
              </button>
              <span className="grow" />
              <button
                type="button"
                className="btn btn-sm btn-secondary mr-1"
                onClick={applyChanges}
              >
                Apply
              </button>
              <button type="button" className="btn btn-sm btn-neutral" onClick={closeModal}>
                Close
              </button>
            </form>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button type="button" onClick={closeModal}>
            close
          </button>
        </form>
      </dialog>
    </div>
  );
}
